import { useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/lib/api-client";
import type { PaperListResponse } from "@/types/paper";

interface SummarizeResponse {
  summary: string;
  error: string | null;
  /** 'user' when the summary was generated from the user's uploaded PDF,
   *  'canonical' when generated from the canonical OA full text. */
  source?: "user" | "canonical";
}

interface ExplainResponse {
  explanation: string;
  error: string | null;
}

export function useSummarizePaper(conceptId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (paperId: string) =>
      apiClient
        .post(`api/concepts/${conceptId}/papers/${paperId}/summarize`, {
          timeout: 120_000,
        })
        .json<SummarizeResponse>(),
    onSuccess: (result, paperId) => {
      if (result.summary) {
        queryClient.setQueriesData<PaperListResponse>(
          { queryKey: ["papers", conceptId] },
          (old) =>
            old
              ? {
                  ...old,
                  papers: old.papers.map((p) =>
                    p.id === paperId
                      ? { ...p, ai_summary: result.summary }
                      : p,
                  ),
                }
              : old,
        );
      }
    },
  });
}

export function useExplainSelection(conceptId: string) {
  return useMutation({
    mutationFn: (data: { paperId: string; text: string }) =>
      apiClient
        .post(
          `api/concepts/${conceptId}/papers/${data.paperId}/explain`,
          {
            json: { text: data.text },
            timeout: 120_000,
          },
        )
        .json<ExplainResponse>(),
  });
}
